import moment from "moment";
import { message } from "antd";
import api from "@/services";
import { center } from "@/utils";

export const p1StartTime = "2021-07-26";
export const p1EndTime = "2021-10-29";
export const p2StartTime = "2022-07-23";
export const p2EndTime = "2022-10-14";
export const p3StartTime = "2023-08-31";
export const p3EndTime = "2023-10-12";

export const layer = {
  state: {
    initial: false,
    passages: [],
    risk: undefined,
    thickness: undefined,
    concentration: undefined,
    fields: undefined,
    imageries: undefined,
    loading: false,
    minDate: "2023-08-31",
    maxDate: "2023-12-19",
  },
  reducers: {
    updateLoading: (state, payload) => {
      return { ...state, loading: payload };
    },
    updateRisk: (state, payload) => {
      return { ...state, risk: payload };
    },
    updatePassages: (state, payload) => {
      return { ...state, passages: payload };
    },
    activePassage: (state, payload) => {
      const { passages } = state;
      const { id } = payload;
      const current = passages.find((p) => p.id === id);
      return {
        ...state,
        fields: {
          time: moment(current.date),
          startPoint: current.startPoint,
          endPoint: current.endPoint,
        },
        passages: passages.map((i) => {
          if (i.id === id) {
            return { ...i, current: true };
          }
          return { ...i, current: false };
        }),
      };
    },
    updateStartPoint: (state, payload) => {
      const { fields } = state;
      return { ...state, fields: { ...fields, startPoint: payload } };
    },
    updateEndPoint: (state, payload) => {
      const { fields } = state;
      return { ...state, fields: { ...fields, endPoint: payload } };
    },
    updateFields: (state, payload) => {
      const { fields } = state;
      const { time, vertex, ...rest } = payload;
      if (time) {
        return {
          ...state,
          fields: { time },
          passages: [],
          risk: undefined,
        };
      }
      return {
        ...state,
        fields: { ...fields, ...rest, ...vertex },
        passages: [],
        risk: undefined,
      };
    },
    addVertex: (state, payload) => {
      const { imageries } = state;
      const polygon = imageries?.polygon;
      const length = polygon ? polygon.length : 0;
      if (length === 1) {
        return {
          ...state,
          imageries: {
            ...imageries,
            polygon: [...polygon, payload],
            drawing: undefined,
          },
        };
      }
      return {
        ...state,
        imageries: {
          ...imageries,
          polygon: [payload],
        },
      };
    },
    updateImageries: (state, payload) => {
      const { imageries } = state;
      return { ...state, imageries: { ...imageries, ...payload } };
    },
    updateDateRange: (state, payload) => {
      return {
        ...state,
        ...payload,
        initial: false,
      };
    },
    failure: (state, payload) => {
      return { ...state, initial: true };
    },
    reset: (state, payload) => {
      return {
        ...state,
        loading: false,
        risk: undefined,
        passages: [],
        fields: undefined,
        imageries: undefined,
      };
    },
  },
  effects: (dispatch) => ({
    async loadDateRange() {
      const response = await fetch("/api/data/forecast_range");
      const { status, msg } = await response.json();
      if (status === "OK") {
        const minDate = moment(msg?.min_date, "YYYY-MM-DD");
        const maxDate = moment(msg?.max_date, "YYYY-MM-DD");
        this.updateDateRange({ minDate, maxDate });
      } else {
        this.failure();
        message.error(msg);
      }
    },
    async loadNavigate(payload) {
      this.updateLoading(true);
      const { time, shipType, vertex } = payload;
      const { startPoint, endPoint } = vertex;
      this.updateRisk({
        url: "/geoserver/arctic/wms",
        name: `v3_irregular_${shipType}_rv_${time.format("YYYY-MM-DD")}`,
      });
      const isOpening =
        time.isBetween(p1StartTime, p1EndTime) ||
        time.isBetween(p2StartTime, p2EndTime) ||
        time.isBetween(p3StartTime, p3EndTime);
      if (isOpening) {
        const response = await fetch(
          `/api/data/user_passage?date=${time.format(
            "YYYY-MM-DD"
          )}&start_lat=${Math.round(startPoint[1])}&start_lon=${Math.round(
            startPoint[0]
          )}&end_lat=${Math.round(endPoint[1])}&end_lon=${Math.round(
            endPoint[0]
          )}&ship_type=${shipType}`
        );
        const { status, msg } = await response.json();
        console.log(status, msg);
        if (status !== "OK") {
          message.error(msg, 10);
        } else {
          const passageResponse = await api.getPassageList();
          const historicalPassages = passageResponse.map((p, i) => {
            return {
              id: p.id,
              name: `Route_${p.added}`,
              date: p.date,
              startPoint: [p.start_lon, p.start_lat],
              endPoint: [p.end_lon, p.end_lat],
              echart: p.echart,
              geojson: p.geojson,
              type: p.type,
              current: i === 0,
              economy: p.economy,
            };
          });
          this.updatePassages(historicalPassages);
        }
      }
      this.updateLoading(false);
    },
    async extract(payload) {
      this.updateLoading(true);
      const c = center(payload?.vertex?.polygon);
      if (!c) {
        this.updateLoading(false);
        return;
      }
      setTimeout(() => {
        dispatch.panel.updateDetection(true);
      }, 4000);
      const [lng, lat] = c.geometry.coordinates;
      await api.addExtractionTask({
        point_lat: lat,
        point_lon: lng,
      });
      this.updateLoading(false);
    },
  }),
  selectors: (slice, createSelector) => ({
    selectLoading() {
      return slice((layer) => layer.loading);
    },
    selectPassages() {
      return slice((layer) => layer.passages);
    },
    selectCurrentLayer() {
      return slice((layer) => layer.passages.find((p) => p.current));
    },
    selectRisk() {
      return slice((layer) => layer.risk);
    },
    selectStartPoint() {
      return slice((layer) => layer.fields?.startPoint);
    },
    selectEndPoint() {
      return slice((layer) => layer.fields?.endPoint);
    },
    selectPoints() {
      return slice((layer) => {
        return {
          startPoint: layer.fields?.startPoint,
          endPoint: layer.fields?.endPoint,
        };
      });
    },
    selectDrawing() {
      return slice((layer) => layer.fields?.drawing);
    },
    selectPolygonDrawing() {
      return slice((layer) => layer.imageries?.drawing);
    },
    selectPolygon() {
      return slice((layer) => layer.imageries?.polygon || []);
    },
    selectFields() {
      return slice((layer) => {
        const shipType = layer.fields?.shipType || "NO_ICE";
        const drawing = layer.fields?.drawing;
        const startPoint = layer.fields?.startPoint;
        const endPoint = layer.fields?.endPoint;
        const result = [];
        result.push({
          name: ["time"],
          value: layer.fields?.time || moment("2023-09-01"),
        });
        result.push({ name: ["shipType"], value: shipType });
        result.push({
          name: ["vertex"],
          value: { drawing, startPoint, endPoint },
        });
        return result;
      });
    },
    selectTime() {
      return slice(
        (layer) => layer.fields?.time?.format("YYYY-MM-DD") || "2023-09-01"
      );
    },
    selectDateRange() {
      return slice((layer) => {
        return { minDate: layer.minDate, maxDate: layer.maxDate };
      });
    },
    selectImageries() {
      return slice((layer) => {
        const drawing = layer.imageries?.drawing;
        const polygon = layer.imageries?.polygon;
        return [
          { name: "time", value: moment("2021-09-10") },
          { name: ["vertex"], value: { drawing, polygon } },
        ];
      });
    },
  }),
};
